// Single source of truth for banner severity styling. Shared by the sticky
// strip (StickyBannerStrip), the popup modal (BannerPopupModal), and the admin
// preview. Extracted from the original MaintenanceBanner so the env maintenance
// banner and the DB-backed dynamic banners render identically.

import { AlertOctagon, AlertTriangle, Info, type LucideIcon } from 'lucide-react'

export type BannerSeverity = 'info' | 'warning' | 'critical'

// One icon per severity, shared by BOTH surfaces. The strip previously
// hardcoded a single inline warning-triangle svg while the popup mapped icons
// properly, so an `info` banner rendered a blue warning triangle in the strip
// and an Info glyph in the modal. Keep this the only place an icon is chosen.
export const SEVERITY_ICONS: Record<BannerSeverity, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  critical: AlertOctagon,
}

export interface SeverityStripClasses {
  container: string
  icon: string
  text: string
  Icon: LucideIcon
}

// Sticky top-strip palette (dark app chrome).
export const STRIP_SEVERITY_CLASSES: Record<BannerSeverity, SeverityStripClasses> = {
  info: {
    container: 'bg-blue-500/20 border-b border-blue-500/30',
    icon: 'text-blue-300',
    text: 'text-blue-100',
    Icon: SEVERITY_ICONS.info,
  },
  warning: {
    container: 'bg-amber-500/20 border-b border-amber-500/30',
    icon: 'text-amber-300',
    text: 'text-amber-100',
    Icon: SEVERITY_ICONS.warning,
  },
  critical: {
    container: 'bg-red-500/25 border-b border-red-500/40',
    icon: 'text-red-200',
    text: 'text-red-50',
    Icon: SEVERITY_ICONS.critical,
  },
}

// Popup accent (severity-tinted title/icon on the dark surface-1 modal card).
// Uses semantic design-system color tokens (info/warning/destructive) rather
// than literal palette shades, and lucide icons rather than emoji.
export interface SeverityPopupClasses {
  accent: string
  Icon: LucideIcon
}

export const POPUP_SEVERITY_CLASSES: Record<BannerSeverity, SeverityPopupClasses> = {
  info: { accent: 'text-info', Icon: SEVERITY_ICONS.info },
  warning: { accent: 'text-warning', Icon: SEVERITY_ICONS.warning },
  critical: { accent: 'text-destructive', Icon: SEVERITY_ICONS.critical },
}

export function coerceSeverity(raw: string | undefined | null): BannerSeverity {
  const normalized = (raw ?? '').trim().toLowerCase()
  if (normalized === 'info') return 'info'
  if (normalized === 'critical') return 'critical'
  return 'warning'
}
